import isUndefined from "lodash/isUndefined";
import { z } from "zod";

import { type NodeId, StackPackInstanceState } from "~/models/dto";
import { ImmutableMap } from "~/tools/immutable-map";
import { errorStatus, idleStatus, loadingStatus, successStatus } from "~/tools/request-status";

import { createReducer } from "../../../../../generic/redux/createReducer";
import { pairActionTypeToReducer } from "../../../../../generic/redux/TypedRedux";
import type {
	ConfirmManualActionsStackPack,
	ConfirmManualActionsStackPackError,
	ConfirmManualActionsStackPackSuccess,
} from "../../../entities/stackPacks/StackPacksActions";
import {
	STACK_PACKS_CONFIRM_MANUAL_ACTIONS_ERROR,
	STACK_PACKS_CONFIRM_MANUAL_ACTIONS_START,
	STACK_PACKS_CONFIRM_MANUAL_ACTIONS_SUCCESS,
} from "../../../entities/stackPacks/StackPacksActionTypes";

import type {
	DeprovisionStackPack,
	DeprovisionStackPackError,
	DeprovisionStackPackSuccess,
	InitializeStackPacksDetails,
	ProvisionStackPackError,
	ProvisionStackPackSuccess,
	SetStackPacksDetailsField,
	UpdateConfigurationStatus,
} from "./StackPacksDetailsActions";
import {
	STACK_PACKS_ADD_CONFIGURATION,
	STACK_PACKS_DEPROVISION_ERROR,
	STACK_PACKS_DEPROVISION_START,
	STACK_PACKS_DEPROVISION_SUCCESS,
	STACK_PACKS_DETAILS_CLEAR_STATE,
	STACK_PACKS_DETAILS_INIT_FIELDS,
	STACK_PACKS_DETAILS_SET_FIELD,
	STACK_PACKS_PROVISION_ERROR,
	STACK_PACKS_PROVISION_START,
	STACK_PACKS_PROVISION_SUCCESS,
	STACK_PACKS_UPDATE_CONFIGURATION,
} from "./StackPacksDetailsActionTypes";
import {
	type StackPackConfigurationState,
	type StackPacksDetailsState,
	initialStackPackConfigurationState,
	initialStackPacksDetailsState,
} from "./StackPacksDetailsState";

const clearState = (): StackPacksDetailsState => initialStackPacksDetailsState;

const setField = (state: StackPacksDetailsState, action: SetStackPacksDetailsField): StackPacksDetailsState => {
	const { fieldName, fieldValue } = action;
	if (isUndefined(action.configId)) {
		const currentFields = state.config?.fields ?? {};
		const conf = state.config || initialStackPackConfigurationState;
		return {
			...state,
			config: {
				...conf,
				fields: { ...currentFields, [fieldName]: fieldValue },
			},
		};
	}
	return {
		...state,
		configs: state.configs.update(action.configId, (configuration) => {
			if (configuration == null) {
				return configuration;
			}
			return { ...configuration, fields: { ...configuration.fields, [fieldName]: fieldValue } };
		}),
	};
};

const initializeStackPackConfigurations = (
	state: StackPacksDetailsState,
	{ stackPackConfigurations }: InitializeStackPacksDetails,
): StackPacksDetailsState => ({
	...state,
	configs: stackPackConfigurations.reduce(
		(acc, el) =>
			acc.set(el.id!, {
				fields: z.record(z.string()).catch({}).parse(el.config),
				stackPacksDeprovisionStatus: undefined,
				stackPacksProvisionStatus: undefined,
				stackPackConfirmStatus: undefined,
			}),
		ImmutableMap.empty<NodeId, StackPackConfigurationState>(),
	),
});

const provisionStart = (state: StackPacksDetailsState): StackPacksDetailsState => ({
	...state,
	config: {
		...state.config!,
		stackPacksProvisionStatus: loadingStatus(),
		stackPacksDeprovisionStatus: undefined,
	},
});

const provisionError = (state: StackPacksDetailsState, { error }: ProvisionStackPackError): StackPacksDetailsState => ({
	...state,
	config: {
		...state.config!,
		stackPacksProvisionStatus: errorStatus(error),
	},
});

const deprovisionStart = (
	state: StackPacksDetailsState,
	{ configurationId }: DeprovisionStackPack,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.update(configurationId, (deprovisionState) => {
		if (deprovisionState == null) {
			return deprovisionState;
		}
		return { ...deprovisionState, stackPacksDeprovisionStatus: loadingStatus() };
	}),
});

const deprovisionSuccess = (
	state: StackPacksDetailsState,
	{ configurationId }: DeprovisionStackPackSuccess,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.delete(configurationId),
});

const provisionSuccess = (
	state: StackPacksDetailsState,
	{ config }: ProvisionStackPackSuccess,
): StackPacksDetailsState => ({
	...state,
	config: undefined,
	configs: state.configs.set(config.id!, {
		...state.config!,
		stackPacksProvisionStatus: successStatus(true),
	}),
});

const deprovisionError = (
	state: StackPacksDetailsState,
	{ error, configurationId }: DeprovisionStackPackError,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.update(configurationId!, (config) => {
		if (config == null) {
			return config;
		}
		return { ...config, stackPacksDeprovisionStatus: errorStatus(error) };
	}),
});

const confirmStart = (
	state: StackPacksDetailsState,
	{ configId }: ConfirmManualActionsStackPack,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.update(configId!, (config) => {
		if (config == null) {
			return config;
		}
		return { ...config, stackPackConfirmStatus: loadingStatus() };
	}),
});

const confirmSuccess = (
	state: StackPacksDetailsState,
	{ configId }: ConfirmManualActionsStackPackSuccess,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.update(configId!, (config) => {
		if (config == null) {
			return config;
		}
		return { ...config, stackPackConfirmStatus: successStatus(true) };
	}),
});

const confirmError = (
	state: StackPacksDetailsState,
	{ configId, error }: ConfirmManualActionsStackPackError,
): StackPacksDetailsState => ({
	...state,
	configs: state.configs.update(configId!, (config) => {
		if (config == null) {
			return config;
		}
		return { ...config, stackPackConfirmStatus: errorStatus(error) };
	}),
});

const updateConfigurationStatus = (
	state: StackPacksDetailsState,
	action: UpdateConfigurationStatus,
): StackPacksDetailsState => {
	const { error, stackPackConfiguration } = action.message;
	const message = error ? error.error.message : "";
	const updated = stackPackConfiguration;

	return {
		...state,
		configs: state.configs.update(updated.id!, (configState) => {
			if (configState == null) {
				return configState;
			}
			return {
				...configState,
				stackPacksProvisionStatus:
					updated.status === StackPackInstanceState.Error ? errorStatus(new Error(message)) : idleStatus(),
			};
		}),
	};
};

const addStackPackConfiguration = (state: StackPacksDetailsState): StackPacksDetailsState => ({
	...state,
	config: {
		fields: {},
		stackPacksDeprovisionStatus: undefined,
		stackPacksProvisionStatus: undefined,
		stackPackConfirmStatus: undefined,
	},
});

export const StackPacksDetailsReducer = createReducer(initialStackPacksDetailsState, {
	...pairActionTypeToReducer(STACK_PACKS_DETAILS_CLEAR_STATE, clearState),
	...pairActionTypeToReducer(STACK_PACKS_DETAILS_SET_FIELD, setField),
	...pairActionTypeToReducer(STACK_PACKS_DETAILS_INIT_FIELDS, initializeStackPackConfigurations),

	...pairActionTypeToReducer(STACK_PACKS_UPDATE_CONFIGURATION, updateConfigurationStatus),

	...pairActionTypeToReducer(STACK_PACKS_PROVISION_START, provisionStart),
	...pairActionTypeToReducer(STACK_PACKS_PROVISION_SUCCESS, provisionSuccess),
	...pairActionTypeToReducer(STACK_PACKS_PROVISION_ERROR, provisionError),

	...pairActionTypeToReducer(STACK_PACKS_DEPROVISION_START, deprovisionStart),
	...pairActionTypeToReducer(STACK_PACKS_DEPROVISION_SUCCESS, deprovisionSuccess),
	...pairActionTypeToReducer(STACK_PACKS_DEPROVISION_ERROR, deprovisionError),

	...pairActionTypeToReducer(STACK_PACKS_CONFIRM_MANUAL_ACTIONS_START, confirmStart),
	...pairActionTypeToReducer(STACK_PACKS_CONFIRM_MANUAL_ACTIONS_SUCCESS, confirmSuccess),
	...pairActionTypeToReducer(STACK_PACKS_CONFIRM_MANUAL_ACTIONS_ERROR, confirmError),

	...pairActionTypeToReducer(STACK_PACKS_ADD_CONFIGURATION, addStackPackConfiguration),
});
